import React, { useEffect, useRef, useState } from 'react';
import { CreditCard, Lock, Loader2, AlertCircle } from 'lucide-react';
import { useAction } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { CHECKOUT_ZERO_TEST, ZERO_TEST_CHARGE } from '../../lib/checkoutZeroTest';

declare global {
  interface Window {
    Clover: new (publicKey: string, options?: { merchantId?: string; locale?: string }) => CloverInstance;
  }
}
interface CloverElements {
  create(type: 'CARD_NUMBER' | 'CARD_DATE' | 'CARD_CVV' | 'CARD_POSTAL_CODE', opts?: object): CloverElement;
}
interface CloverElement { mount(selector: string): void; }
interface CloverInstance {
  elements(): CloverElements;
  createToken(): Promise<{ token?: string; errors?: Record<string, string> }>;
}

interface CloverPaymentFormProps {
  onSubmit: (chargeResult: { chargeId: string; amount: number; status: string; paid: boolean }) => Promise<void>;
  onTokenReady?: (token: string) => void;
  totalAmount: number;
  isProcessing?: boolean;
  description?: string;
  email?: string;
}

const SDK_URL = import.meta.env.VITE_CLOVER_ENV === 'sandbox'
  ? 'https://checkout.sandbox.dev.clover.com/sdk.js'
  : 'https://checkout.clover.com/sdk.js';

const CloverPaymentForm: React.FC<CloverPaymentFormProps> = ({
  onSubmit,
  onTokenReady,
  totalAmount,
  isProcessing = false,
  description,
  email,
}) => {
  const chargeCard = useAction(api.payments.chargeCard);
  const [sdkReady, setSdkReady] = useState(false);
  const [clover, setClover] = useState<CloverInstance | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const mounted = useRef(false);

  // Load SDK script once
  useEffect(() => {
    if (document.querySelector(`script[src="${SDK_URL}"]`)) {
      if (window.Clover) setSdkReady(true);
      return;
    }
    const s = document.createElement('script');
    s.src = SDK_URL;
    s.async = true;
    s.onload = () => setSdkReady(true);
    s.onerror = () => setError('Impossible de charger le SDK Clover.');
    document.head.appendChild(s);
  }, []);

  // Show the Clover security footer while this form is mounted; hide it on unmount.
  useEffect(() => {
    const showCloverFooter = () => {
      // Clover SDK injects #clover-footer (and sometimes iframe siblings) directly into <body>
      ['#clover-footer', '[id^="clover-footer"]', '[class*="clover-footer"]'].forEach((sel) => {
        document.querySelectorAll<HTMLElement>(sel).forEach((el) => {
          el.style.removeProperty('display');
        });
      });
    };

    // The footer may not be injected immediately — watch for it
    const observer = new MutationObserver(showCloverFooter);
    observer.observe(document.body, { childList: true, subtree: false });

    // Also try right away in case it already exists
    showCloverFooter();

    return () => {
      observer.disconnect();
      // Move footer to bottom of body (in case it was displaced) and hide
      ['#clover-footer', '[id^="clover-footer"]', '[class*="clover-footer"]'].forEach((sel) => {
        document.querySelectorAll<HTMLElement>(sel).forEach((el) => {
          el.style.display = 'none';
        });
      });
    };
  }, []);

  // Mount iframe fields once SDK is ready
  useEffect(() => {
    if (!sdkReady || mounted.current) return;

    const publicKey = import.meta.env.VITE_CLOVER_PUBLIC_KEY;
    const merchantId = import.meta.env.VITE_CLOVER_MERCHANT_ID;

    if (!publicKey) { setError('VITE_CLOVER_PUBLIC_KEY manquant'); return; }

    try {
      const instance = new window.Clover(publicKey, { merchantId, locale: 'fr-CA' });
      const els = instance.elements();

      const styles = { styles: { input: { height: '36px', padding: '0 10px', fontSize: '14px', color: '#111827', 'font-family': 'inherit' } } };
      els.create('CARD_NUMBER', styles).mount('#clover-number');
      els.create('CARD_DATE', styles).mount('#clover-date');
      els.create('CARD_CVV', styles).mount('#clover-cvv');
      els.create('CARD_POSTAL_CODE', styles).mount('#clover-postal');

      setClover(instance);
      mounted.current = true;
    } catch (e: any) {
      setError("Erreur d'initialisation Clover : " + e?.message);
    }

    return () => { mounted.current = false; };
  }, [sdkReady]);

  const handlePay = async () => {
    if (!clover) return;
    setError(null);
    setProcessing(true);

    try {
      const result = await clover.createToken();
      console.log('[Clover] createToken result:', result);

      if (result.errors && Object.keys(result.errors).length > 0) {
        setError(Object.values(result.errors).join(' | '));
        return;
      }
      if (!result.token) {
        setError('Impossible de tokeniser la carte. Vérifiez vos informations.');
        return;
      }

      onTokenReady?.(result.token);

      const charge = await chargeCard({
        token: result.token,
        amountCents: Math.round(totalAmount * 100),
        description,
        email,
      });

      await onSubmit(charge);
    } catch (e: any) {
      console.error('[Clover] Error:', e);
      setError(e?.message ?? 'Une erreur est survenue.');
    } finally {
      setProcessing(false);
    }
  };

  const isLoading = processing || isProcessing;
  const skipCardCharge = CHECKOUT_ZERO_TEST || totalAmount <= 0;

  const handleZeroTestConfirm = async () => {
    setError(null);
    setProcessing(true);
    try {
      await onSubmit(ZERO_TEST_CHARGE);
    } catch (e: unknown) {
      console.error('[Checkout zero test]', e);
      setError((e as { message?: string })?.message ?? 'Une erreur est survenue.');
    } finally {
      setProcessing(false);
    }
  };

  if (skipCardCharge) {
    return (
      <div className="space-y-4">
        {CHECKOUT_ZERO_TEST && totalAmount > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Mode test activé — le paiement est forcé à 0&nbsp;$, la réservation et le courriel seront quand même envoyés.
          </div>
        )}
        <button
          type="button"
          onClick={handleZeroTestConfirm}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 py-3 px-6 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white font-semibold rounded-xl transition-colors"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" /> Traitement…
            </>
          ) : (
            <>Confirmer la commande (0,00&nbsp;$)</>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <CreditCard className="w-5 h-5 text-primary-600" />
        <span className="font-semibold text-gray-800">Informations de paiement</span>
        <div className="ml-auto flex items-center gap-1 text-xs text-gray-500">
          <Lock className="w-3.5 h-3.5" /> Sécurisé par Clover
        </div>
      </div>

      {!sdkReady && !error && (
        <div className="flex items-center gap-2 py-4 text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      )}

      <div className={!sdkReady ? 'hidden' : 'space-y-3'}>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Numéro de carte</label>
          <div id="clover-number" className="border border-gray-300 rounded-lg bg-white overflow-hidden" style={{ height: '40px' }} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Expiration</label>
            <div id="clover-date" className="border border-gray-300 rounded-lg bg-white overflow-hidden" style={{ height: '40px' }} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">CVV</label>
            <div id="clover-cvv" className="border border-gray-300 rounded-lg bg-white overflow-hidden" style={{ height: '40px' }} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Code postal</label>
            <div id="clover-postal" className="border border-gray-300 rounded-lg bg-white overflow-hidden" style={{ height: '40px' }} />
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {error}
        </div>
      )}

      <button
        type="button"
        onClick={handlePay}
        disabled={!sdkReady || isLoading}
        className="w-full flex items-center justify-center gap-2 py-3 px-6 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white font-semibold rounded-xl transition-colors"
      >
        {isLoading ? <><Loader2 className="w-5 h-5 animate-spin" /> Traitement…</> : <><Lock className="w-4 h-4" /> Payer {totalAmount.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</>}
      </button>
    </div>
  );
};

export default CloverPaymentForm;
